import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useHistory, useParams, useLocation } from '../../hooks/useRouterCompat';
import { Trans, t } from '@lingui/macro';
import { Menu as MenuIcon, LogOut, Users, PlusCircle, LayoutGrid, User, Calendar, Home, PanelRight, Loader2, Search, Star, X, BellOff, Settings2, MessageSquare } from 'lucide-react';
import { MyNotifs } from '../../services/MyNotifs';

import { useAppUIContext } from '../../context/AppUIContext';
import { RoomSimplified } from '../../models/Room';
import { Workspace, canJoinAsMember } from '../../models/Workspace';
import { getMemberPermissions, WorkspacePermissions } from '../../models/WorkspacePermissions';
import { Fire } from '../../services/FireClient';
import { formatBytes } from '../../services/StorageQuotaService';
import { My } from '../../services/My';
import { MyRoomOptionModal } from '../components/MyRoomOptionModal';
import { Room } from '../../models/Room';
import { LocalStorage } from '../../services/Utils/LocalStorage';
import { UtilApp } from '../../services/Utils/UtilApp';
import { useResponsive } from '../../hooks/useResponsive';


import CreateRoomModal from '../components/CreateRoomModal';
import WSAdminRoomsPage from './admin/WSAdminRoomsPage';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { useToast } from '../../components/ui/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import { AdminView, WSAdminSidebarContent } from './admin/WSAdminSidebarContent';
import WSAdminSettings from './admin/WSAdminSettings';
import WSAdminMembers from './admin/WSAdminMembers';
import WSAdminStorage from './admin/WSAdminStorage';
import WSAdminInquiry from './admin/WSAdminInquiry';
import WSAdminCompanyPage from './admin/WSAdminCompanyPage';
import WSAdminBoardPolicyPage from './admin/WSAdminBoardPolicyPage';
import WSAdminGigapodPage from './admin/WSAdminGigapodPage';
import { IconHash, IconUserPlus } from '../components/FigmaIcons';

const WorkspaceViewPage: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { workspaces, rooms: contextRooms, setIsMobileMenuOpen, activeWorkspaceId, setActiveWorkspaceId } = useAppUIContext();
  const [counter, setCounter] = useState(0);
  // Initialize workspace from cache if available to avoid flickering
  const [workspace, setWorkspace] = useState<Workspace | undefined>(() => workspaces.find(w => w.id === workspaceId));
  const [rooms, setRooms] = useState<RoomSimplified[]>([]);
  const [inProgress, setInProgress] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();
  const history = useHistory();
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const { isMobileOrTablet } = useResponsive();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [canViewAdmin, setCanViewAdmin] = useState(false);
  const [roomListTab, setRoomListTab] = useState<'joined' | 'available'>('joined');
  const [isLoadingAvailable, setIsLoadingAvailable] = useState(false);
  const [roomSearchFilter, setRoomSearchFilter] = useState<string>('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [notifsByRoom] = useState(new Map<string, number>());
  const [_, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const [optionModalRoom, setOptionModalRoom] = useState<RoomSimplified | null>(null);

  // URL workspaceId 기반 권한 (보안: context permissions 대신 사용)
  const [urlPerms, setUrlPerms] = useState<WorkspacePermissions | null>(null);
  const [myRole, setMyRole] = useState<string | null>(null);

  // URL의 workspaceId와 activeWorkspaceId 동기화
  useEffect(() => {
    if (workspaceId && workspaceId !== activeWorkspaceId) {
      setActiveWorkspaceId(workspaceId);
    }
    // 워크스페이스 변경 시 즉시 캐시된 정보를 state에 반영 (잔상 방지)
    const cachedWs = workspaces.find(w => w.id === workspaceId);
    if (cachedWs) setWorkspace(cachedWs);
  }, [workspaceId, workspaces]);

  // URL의 workspaceId 기반으로 권한을 직접 로드
  useEffect(() => {
    if (!workspaceId) {
      setUrlPerms(null);
      return;
    }
    Fire.ws.getMyMember(workspaceId).then(myMember => {
      if (myMember) {
        setUrlPerms(getMemberPermissions(myMember));
        setMyRole(myMember.role || null);
      } else {
        // 멤버도 게스트도 아닌 외부 유저 → 워크스페이스 뷰 빈 화면 대신 루트로 이동
        // (로그인 유저일 때만 — My.user 미로드 시 getMyMember 가 null 을 반환하는 race 방지)
        setUrlPerms(null);
        setMyRole(null);
        if (My.user?.id) {
          // 진입 효과(setActiveWorkspaceId(URL ws))가 활성 워크스페이스(+localStorage)를 외부 ws 로 오염시킴.
          // 내 워크스페이스로 복구하지 않으면 /main 이 외부 ws 를 active 로 들고 있어 여전히 빈 화면.
          const fallbackWs = workspaces.find(w => w.id !== workspaceId);
          setActiveWorkspaceId(fallbackWs?.id ?? null);
          history.replace('/');
        }
      }
    }).catch(() => { setUrlPerms(null); setMyRole(null); }); // 조회 실패(네트워크 등)는 리다이렉트 안 함
  }, [workspaceId]);

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const viewParam = query.get('view') as AdminView;
  const [currentView, setCurrentView] = useState<AdminView>(viewParam || 'overview');

  useEffect(() => {
    let unsubNotifs = MyNotifs.subscribeToNotificationUpdates((_) => {
      setCounter(MyNotifs.NrOfNew);
      // 방별 알림 카운트 업데이트
      notifsByRoom.clear();
      MyNotifs.NotificationsByRoom.forEach((notifs, roomId) => {
        // 노출 대상(음소거·외부 API 비활성화 제외)만 카운트 — 통지센터·좌측 목록과 동일 기준
        const filteredCount = notifs.filter(n => n.isNew && n.type != 'RoomAdded' && n.type != 'CallStart' && MyNotifs.isVisibleInCenter(n, My.RoomSettings)).length;
        if (filteredCount > 0) notifsByRoom.set(roomId, filteredCount);
      });
      forceUpdate();
    });
    return () => { unsubNotifs(); }
  }, []);

  // Update URL when view changes
  const handleViewChange = (view: AdminView) => {
    setCurrentView(view);
    history.push(`/ws/${workspaceId}?view=${view}`);
  };

  // Sync state with URL if it changes externally (e.g. back button or workspace switch)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view') as AdminView;
    const targetView = view || 'overview';
    if (targetView !== currentView) {
      setCurrentView(targetView);
    }
  }, [location.search]);

  // Alert State
  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    title: React.ReactNode;
    description?: React.ReactNode;
    confirmText?: string;
    onConfirm?: () => void;
  }>({ open: false, title: "" });

  const prevWsRef = useRef<string | undefined>();
  useEffect(() => {
    // 워크스페이스가 전환된 직후에는 urlPerms가 이전 것일 수 있으므로 스킵
    if (prevWsRef.current !== workspaceId) {
      prevWsRef.current = workspaceId;
      return;
    }
    reload();
  }, [workspaceId, urlPerms]);

  async function reload(silent = false) {
    if (!silent) {
      setInProgress(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      // 워크스페이스 정보만 가져옴 (방 목록은 contextRooms 사용)
      const foundWs = await Fire.ws.getWorkspace(workspaceId || "");

      if (foundWs) setWorkspace(foundWs);

      // 관리자 패널 접근 권한 확인 (URL 기반 permissions 사용)
      const isOwnerCheck = foundWs?.owner?.id === My.user.id;
      let hasAdminAccess = isOwnerCheck;

      // URL workspaceId 기반 권한 사용 (context permissions가 아닌 직접 로드한 권한)
      if (urlPerms) {
        hasAdminAccess = urlPerms.canViewAdminPanel;
      }
      setCanViewAdmin(hasAdminAccess);

      // 권한 체크: admin view 접근 불가 시 overview로 리다이렉트
      const params = new URLSearchParams(window.location.search);
      const currentViewInUrl = params.get('view') as AdminView;

      if (!hasAdminAccess && currentViewInUrl && currentViewInUrl !== 'overview') {
        setCurrentView('overview');
        history.replace(`/ws/${workspaceId}`);
      }
    } catch (e) {
      console.error(e);
      toast({ title: "Failed to load workspace" });
    } finally {
      setInProgress(false);
      setIsRefreshing(false);
    }
  }

  const handleJoinClick = (room: RoomSimplified) => {
    if (hasJoined(room.id)) {
      toast({ title: t`You have already joined this channel` });
    } else {
      setAlertConfig({
        open: true,
        title: t`Would you like to join this channel?`,
        confirmText: t`Join`,
        onConfirm: () => confirmJoin(room)
      });
    }
  }

  const confirmJoin = async (room: RoomSimplified) => {
    setInProgress(true);
    try {
      const res = await Fire.ws.joinRoomViaWorkspace({ roomId: room.id });

      if (res.status === 'NOT_MEMBER') {
        toast({ title: t`You are not a member of this workspace`, duration: 3000 });
      } else if (res.status === 'SUCCESS') {
        history.push(`/channel/${room.id}`);
      } else {
        toast({ title: `Error joining room`, duration: 3000 });
      }
    } catch (e) {
      toast({ title: `Error joining room` });
    } finally {
      setInProgress(false);
      setAlertConfig(prev => ({ ...prev, open: false }));
    }
  }

  function hasJoined(roomId: string) {
    return My.Rooms.has(roomId);
  }

  const handleLeaveWorkspace = () => {
    if (!workspace?.id) return;
    setAlertConfig({
      open: true,
      title: t`Would you like leave this workspace?`,
      confirmText: t`Leave now`,
      onConfirm: async () => {
        setInProgress(true);
        try {
          await Fire.ws.leaveWorkspace(workspace.id!, My.user.id);
          const roomid = LocalStorage.get("last-visited-room")
          if (roomid) history.replace(`/channel/${roomid}`);
          else history.replace('/main');
        } catch (e) {
          toast({ title: "Failed to leave workspace" });
        } finally {
          setInProgress(false);
          setAlertConfig(prev => ({ ...prev, open: false }));
        }
      }
    });
  }

  const handleUpgradeToMember = async () => {
    if (!workspace?.id) return;
    setInProgress(true);
    try {
      const res = await Fire.ws.joinWorkspace({ workspaceId: workspace.id, invitationKey: null });
      if (res === 'JOINED') {
        toast({ title: t`You are now a member of this workspace!`, duration: 3000 });
        // 권한 및 역할 다시 로드
        const myMember = await Fire.ws.getMyMember(workspace.id);
        if (myMember) {
          setUrlPerms(getMemberPermissions(myMember));
          setMyRole(myMember.role || null);
        }
        await reload(true);
      } else if (res === 'ALREADY_MEMBER') {
        toast({ title: t`You are already a member`, duration: 2000 });
      } else {
        toast({ title: t`Unable to join as member`, duration: 3000 });
      }
    } catch (e) {
      toast({ title: t`Failed to upgrade membership` });
    } finally {
      setInProgress(false);
    }
  }

  const isOwner = workspace?.owner?.id === My.user.id;
  const isAdmin = canViewAdmin;  // permissions 기반 관리자 패널 접근 권한



  return (
    <div className="flex flex-col h-full overflow-hidden bg-background md:rounded-panel md:border md:border-border">
      {/* Header */}
      <header className="z-40 bg-background border-b min-w-0 flex items-center justify-between px-4 py-4 shrink-0 shadow-sm relative overflow-hidden">
        <div className="flex items-center gap-2 w-full">
          <div className="shrink-0 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              data-cy='menu'
              onClick={() => setIsMobileMenuOpen(true)}
              className="relative"
            >
              <MenuIcon className="h-5 w-5" />
              {!!counter && (
                <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 rounded-full text-meta-sm">
                  {counter}
                </Badge>
              )}
            </Button>
          </div>

          <div className="flex flex-1 items-center justify-between min-w-0">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {workspace?.iconUrl ? (
                <img src={workspace.iconUrl} className="w-9 h-9 rounded-xl object-cover shrink-0 border border-border" alt="" />
              ) : workspace ? (
                <div className="w-9 h-9 rounded-xl bg-[rgba(255,127,0,0.1)] text-primary flex items-center justify-center font-bold text-sm shrink-0 border border-[rgba(255,127,0,0.1)]">
                  {workspace.name.charAt(0)}
                </div>
              ) : null}
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-semibold truncate">{workspace?.name || <Trans>Workspace</Trans>}</h1>
                {workspace && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{workspace.id}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {workspace && !isOwner && (
                <Button variant="ghost" size="sm" onClick={handleLeaveWorkspace}>
                  <LogOut className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline"><Trans>Leave</Trans></span>
                </Button>
              )}
              {/* Admin Menu Trigger for Mobile */}
              {workspace && isAdmin && isMobileOrTablet && (
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                  <SheetTrigger asChild>
                    <Button variant="icon-box">
                      <PanelRight className="w-5 h-5 text-primary" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[280px] p-0">
                    <WSAdminSidebarContent
                      workspace={workspace}
                      currentView={currentView}
                      onChangeView={handleViewChange}
                      onClose={() => setSheetOpen(false)}
                    />
                  </SheetContent>
                </Sheet>
              )}
            </div>
          </div>
        </div>

        {/* Top Progress Bar (Silent refresh) */}
        {isRefreshing && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-blue-50 overflow-hidden z-10">
            <div className="h-full bg-blue-500 animate-shimmer w-full" style={{
              backgroundImage: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite'
            }} />
          </div>
        )}
        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Content — 고정 높이 컨테이너, 내부 스크롤 */}
        <main className="flex-1 overflow-hidden bg-background">
          <div className="h-full overflow-y-auto">
            {inProgress ? (
              <div className="flex flex-col items-center justify-center py-20 w-full h-full">
                <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground font-medium"><Trans>Loading workspace information...</Trans></p>
              </div>
            ) : (
              workspace && (
                <>
                  {currentView === 'overview' && (
                    <div className="p-4 md:p-6 lg:p-8 w-full space-y-8">
                      {/* About Workspace Card */}
                      <Card className="border-border">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-2 mb-4">
                            <LayoutGrid className="w-5 h-5 text-muted-foreground" />
                            <h3 className="text-lg font-bold text-foreground"><Trans>About Workspace</Trans></h3>
                          </div>

                          <div className="space-y-3">
                            {/* Owner */}
                            <div className="flex items-start gap-3">
                              <User className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-medium text-foreground">{workspace.owner?.name || t`Unknown`}</p>
                                  <Badge variant="outline" className="text-xs h-5">
                                    <Trans>Owner</Trans>
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">{workspace.owner?.email || ''}</p>
                              </div>
                            </div>

                            {/* Created Date */}
                            <div className="flex items-center gap-3">
                              <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-card-foreground">
                                  <Trans>Created</Trans>: {UtilApp.dateStrToYmd(workspace.created, My.user.lang)}
                                </p>
                              </div>
                            </div>

                            {/* ID & Tier */}
                            <div className="flex items-center gap-3">
                              <IconHash className="w-4 h-4 text-muted-foreground shrink-0" />
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <p className="text-sm text-card-foreground font-mono">{workspace.id}</p>
                                {workspace.tier && (
                                  <>
                                    <span className="text-muted-foreground">·</span>
                                    <Badge variant="secondary" className="text-xs">
                                      {workspace.tier}
                                    </Badge>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Description */}
                            {workspace.description?.trim() && (
                              <div className="pt-3 border-t">
                                <div className="prose prose-sm max-w-none text-card-foreground">
                                  <div dangerouslySetInnerHTML={{ __html: UtilApp.getTextAsHTML(workspace.description, My.user.id) }} />
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Guest 멤버 승격 안내 — 정책상 셀프 승격 가능할 때만 (공개 또는 도메인 제한+허용 도메인) */}
                      {myRole === 'guest' && canJoinAsMember(workspace, My.user.email, false) && (
                        <Card className="border-green-200 bg-green-50/50">
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between gap-4">
                              <div className="space-y-1">
                                <h3 className="text-sm font-semibold text-green-900"><Trans>You are currently a guest in this workspace.</Trans></h3>
                                <p className="text-xs text-green-700">
                                  <Trans>Join as a member to access listed channels and create your own channels.</Trans>
                                </p>
                              </div>
                              <Button
                                onClick={handleUpgradeToMember}
                                disabled={inProgress}
                                className="bg-green-600 hover:bg-green-700 text-on-primary-text shrink-0"
                              >
                                <IconUserPlus className="w-4 h-4 mr-2" />
                                <Trans>Join as Member</Trans>
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {urlPerms?.canAccessWorkspaceView !== false && <div>
                        {/* 탭 헤더 */}
                        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-1 border-b overflow-x-auto">
                            <button
                              onClick={() => setRoomListTab('joined')}
                              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold transition-all border-b-2 -mb-px whitespace-nowrap ${
                                roomListTab === 'joined'
                                  ? 'border-border text-foreground'
                                  : 'border-transparent text-muted-foreground hover:text-card-foreground'
                              }`}
                            >
                              <Home className="w-4 h-4" />
                              <Trans>My Channels</Trans>
                              {(() => {
                                const myContextRooms = contextRooms.filter(r => r.workspaceId === workspaceId);
                                // mute(푸시 비활성화) 채널은 합계 배지에서 제외 — 통지벨·개별 회색 배지와 일치
                                const totalNotifs = myContextRooms.reduce((sum, r) => sum + (My.RoomSettings.get(r.id)?.disablePushNotifications ? 0 : (notifsByRoom.get(r.id) ?? 0)), 0);
                                return totalNotifs > 0 ? (
                                  <Badge variant="destructive" className="ml-1 h-5 px-1.5 text-meta-sm rounded-full">
                                    {totalNotifs > 99 ? '99+' : totalNotifs}
                                  </Badge>
                                ) : null;
                              })()}
                            </button>
                            <button
                              onClick={async () => {
                                setRoomListTab('available');
                                setIsLoadingAvailable(true);
                                try {
                                  const res = await Fire.ws.getWorkspaceRooms({ workspaceId: workspaceId || '' });
                                  if (res.status === 'SUCCESS') {
                                    setRooms(res.rooms.sort((a, b) => (a.created ?? 0) < (b.created ?? 0) ? 1 : -1));
                                  }
                                } catch (e) {
                                  console.error('Failed to refresh rooms:', e);
                                } finally {
                                  setIsLoadingAvailable(false);
                                }
                              }}
                              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold transition-all border-b-2 -mb-px whitespace-nowrap ${
                                roomListTab === 'available'
                                  ? 'border-blue-600 text-blue-600'
                                  : 'border-transparent text-muted-foreground hover:text-card-foreground'
                              }`}
                            >
                              <Users className="w-4 h-4" />
                              <Trans>Available Channels</Trans>
                            </button>
                            <div className="flex-1" />
                            {urlPerms?.canCreateRoom !== false && (
                              <button
                                onClick={() => setShowCreateRoomModal(true)}
                                className="flex items-center justify-center w-8 h-8 rounded-lg text-primary hover:text-primary hover:bg-primary/10 transition-colors -mb-px"
                                title={t`New Channel`}
                              >
                                <PlusCircle className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* 참가중인 방 */}
                        {roomListTab === 'joined' && (
                          <>
                            {/* 검색 & 필터 */}
                            <div className="flex items-center gap-2 mb-4">
                              <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <input
                                  type="text"
                                  className="w-full h-9 pl-9 pr-8 text-sm text-foreground rounded-lg border border-border bg-muted focus:bg-muted focus:border-primary focus:outline-none transition-colors placeholder:text-muted-foreground"
                                  placeholder={t`Search channels...`}
                                  value={roomSearchFilter}
                                  onChange={e => setRoomSearchFilter(e.target.value)}
                                />
                                {roomSearchFilter && (
                                  <button className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setRoomSearchFilter('')}>
                                    <X className="w-4 h-4 text-muted-foreground hover:text-card-foreground" />
                                  </button>
                                )}
                              </div>
                              <Button
                                variant={showFavoritesOnly ? "default" : "outline"}
                                size="sm"
                                className="h-9 shrink-0"
                                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                              >
                                <Star className={`w-4 h-4 ${showFavoritesOnly ? 'fill-white' : 'text-muted-foreground'}`} />
                              </Button>
                            </div>

                            {/* 일반 방 섹션 */}
                            {(() => {
                              const myRooms = contextRooms.filter(r => r.workspaceId === workspaceId && !r.isOneToOne && r.visibility !== 'personal')
                                .filter(r => !roomSearchFilter || (() => { const k = roomSearchFilter.toLowerCase(); return r.name.toLowerCase().includes(k) || r.users?.some(u => u.name?.toLowerCase().includes(k) || u.email?.toLowerCase().includes(k)); })())
                                .filter(r => !showFavoritesOnly || My.RoomSettings.get(r.id)?.favoriteRoomInMyList);
                              return myRooms.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                  {myRooms.map(room => {
                                    const roomNotifs = notifsByRoom.get(room.id) ?? 0;
                                    const isFav = My.RoomSettings.get(room.id)?.favoriteRoomInMyList;
                                    const isMuted = My.RoomSettings.get(room.id)?.disablePushNotifications;
                                    return (
                                      <Card key={room.id} className="group hover:shadow-lg transition-all duration-200 border-border flex flex-col h-full cursor-pointer" onClick={() => history.push(`/channel/${room.id}`)}>
                                        <CardContent className="p-5 flex flex-col flex-1 gap-3">
                                          <div className="flex items-start gap-4">
                                            {room.profileImageUrl ? (
                                              <Avatar className="w-12 h-12 rounded-2xl shadow-sm shrink-0">
                                                <AvatarImage src={room.profileImageUrl} className="object-cover" />
                                                <AvatarFallback className="text-lg bg-muted">{room.name.substring(0, 2)}</AvatarFallback>
                                              </Avatar>
                                            ) : (
                                              <Avatar className="w-12 h-12 shrink-0 rounded-2xl">
                                                <AvatarFallback className="text-xl font-bold bg-primary text-primary-foreground">
                                                  {room.name.substring(0, 1).toUpperCase()}
                                                </AvatarFallback>
                                              </Avatar>
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2">
                                                <h3 className="font-bold text-base text-foreground truncate leading-tight"># {room.name}</h3>
                                                {isFav && <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500 shrink-0" />}
                                                {isMuted && <BellOff className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                                              </div>
                                              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-1">
                                                <div className="flex items-center gap-1">
                                                  <User className="w-3 h-3 text-muted-foreground" />
                                                  <span>{room.users.length}</span>
                                                </div>
                                                <span className="text-muted-foreground">•</span>
                                                <span>{UtilApp.dateStrToDay(room.created, My.user.lang)}</span>
                                                {(room.storageUsage && room.storageUsage > 0) && (
                                                  <>
                                                    <span className="text-muted-foreground">•</span>
                                                    <span className="font-medium text-primary">{formatBytes(room.storageUsage)}</span>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                              {/* roomNotifs 는 노출 대상만 카운트 → 음소거 방은 뱃지 안 뜸 */}
                                              {roomNotifs > 0 && (
                                                <Badge variant="destructive" className="h-5 px-1.5 text-meta-sm rounded-full">
                                                  {roomNotifs}
                                                </Badge>
                                              )}
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="w-7 h-7 transition-opacity"
                                                onClick={(e) => { e.stopPropagation(); setOptionModalRoom(room); }}
                                              >
                                                <Settings2 className="w-3.5 h-3.5 text-muted-foreground" />
                                              </Button>
                                            </div>
                                          </div>
                                          {room.description && (
                                            <p className="text-sm text-muted-foreground line-clamp-2">{room.description}</p>
                                          )}
                                        </CardContent>
                                      </Card>
                                    )
                                  })}
                                </div>
                              ) : (
                                !roomSearchFilter && !showFavoritesOnly ? (
                                  <div className="text-center py-12 text-muted-foreground bg-background rounded-xl border border-dashed">
                                    <Home className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                                    <p className="text-base font-medium"><Trans>No channels joined yet.</Trans></p>
                                    <p className="text-sm text-muted-foreground mt-1"><Trans>Check the "Available Channels" tab to join channels.</Trans></p>
                                  </div>
                                ) : (
                                  <div className="text-center py-8 text-muted-foreground">
                                    <Search className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                                    <p className="text-sm"><Trans>No matching channels found.</Trans></p>
                                  </div>
                                )
                              );
                            })()}

                            {/* 1:1 대화방 섹션 */}
                            {(() => {
                              const oneToOneRooms = contextRooms.filter(r => r.isOneToOne && r.workspaceId === workspaceId)
                                .filter(r => !roomSearchFilter || (() => { const k = roomSearchFilter.toLowerCase(); return r.name.toLowerCase().includes(k) || r.users?.some(u => u.name?.toLowerCase().includes(k) || u.email?.toLowerCase().includes(k)); })())
                                .filter(r => !showFavoritesOnly || My.RoomSettings.get(r.id)?.favoriteRoomInMyList);
                              if (oneToOneRooms.length === 0) return null;
                              return (
                                <div className="mt-8">
                                  <div className="flex items-center gap-2 mb-4">
                                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider"><Trans>Direct Messages</Trans></h3>
                                    <div className="flex-1 border-b border-border" />
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {oneToOneRooms.map(room => {
                                      const otherUser = room.users?.find(u => u.id !== My.user.id);
                                      const roomNotifs = notifsByRoom.get(room.id) ?? 0;
                                      const isFav = My.RoomSettings.get(room.id)?.favoriteRoomInMyList;
                                      const isMuted = My.RoomSettings.get(room.id)?.disablePushNotifications;
                                      return (
                                        <Card key={room.id} className="group hover:shadow-md transition-all duration-200 border-border cursor-pointer" onClick={() => history.push(`/channel/${room.id}`)}>
                                          <CardContent className="p-4 flex items-center gap-3">
                                            <Avatar className="w-10 h-10 shrink-0">
                                              <AvatarImage src={otherUser?.photoUrl ?? undefined} />
                                              <AvatarFallback className="text-sm bg-muted">{otherUser?.name?.substring(0, 1) || '?'}</AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-1.5">
                                                <span className="font-semibold text-sm text-foreground truncate">{otherUser?.name || room.name}</span>
                                                {isFav && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 shrink-0" />}
                                                {isMuted && <BellOff className="w-3 h-3 text-muted-foreground shrink-0" />}
                                              </div>
                                              {otherUser?.email && <p className="text-xs text-muted-foreground truncate">{otherUser.email}</p>}
                                            </div>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                              {/* roomNotifs 는 노출 대상만 카운트 → 음소거 방은 뱃지 안 뜸 */}
                                              {roomNotifs > 0 && (
                                                <Badge variant="destructive" className="h-5 px-1.5 text-meta-sm rounded-full">
                                                  {roomNotifs}
                                                </Badge>
                                              )}
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="w-7 h-7 transition-opacity"
                                                onClick={(e) => { e.stopPropagation(); setOptionModalRoom(room); }}
                                              >
                                                <Settings2 className="w-3.5 h-3.5 text-muted-foreground" />
                                              </Button>
                                            </div>
                                          </CardContent>
                                        </Card>
                                      )
                                    })}
                                  </div>
                                </div>
                              );
                            })()}
                          </>
                        )}

                        {/* 참가 가능한 방 */}
                        {roomListTab === 'available' && (
                          <>
                            {isLoadingAvailable ? (
                              <div className="flex flex-col items-center justify-center py-16">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-3" />
                                <p className="text-sm text-muted-foreground"><Trans>Loading available channels...</Trans></p>
                              </div>
                            ) : (
                              <>
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                  {rooms.filter(r => r.visibility === 'listed' && !r.isOneToOne && !hasJoined(r.id)).map(room => {
                                    const creator = room.users?.find(u => u.id === room.creatorId);
                                    return (
                                      <Card key={room.id} className="group hover:shadow-lg transition-all duration-200 border-border flex flex-col h-full">
                                        <CardContent className="p-5 flex flex-col flex-1 gap-4">
                                          <div className="flex items-start gap-4">
                                            {room.profileImageUrl ? (
                                              <Avatar className="w-14 h-14 rounded-2xl shadow-sm shrink-0">
                                                <AvatarImage src={room.profileImageUrl} className="object-cover" />
                                                <AvatarFallback className="text-lg bg-muted">{room.name.substring(0, 2)}</AvatarFallback>
                                              </Avatar>
                                            ) : (
                                              <Avatar className="w-14 h-14 shrink-0 rounded-2xl">
                                                <AvatarFallback className="text-xl font-bold bg-primary text-primary-foreground">
                                                  {room.name.substring(0, 1).toUpperCase()}
                                                </AvatarFallback>
                                              </Avatar>
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <h3 className="font-bold text-lg text-foreground truncate leading-tight mb-2"># {room.name}</h3>
                                              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                                {creator && (
                                                  <div className="flex items-center gap-1.5 bg-muted/50 px-2 py-0.5 rounded-full border border-border">
                                                    <Avatar className="w-4 h-4">
                                                      <AvatarImage src={creator.photoUrl ?? undefined} />
                                                      <AvatarFallback className="text-meta-sm">{creator.name[0]}</AvatarFallback>
                                                    </Avatar>
                                                    <span className="font-medium text-foreground max-w-20 truncate">{creator.name}</span>
                                                  </div>
                                                )}
                                                <span className="text-muted-foreground">•</span>
                                                <span>{UtilApp.dateStrToDay(room.created, My.user.lang)}</span>
                                                {(room.storageUsage && room.storageUsage > 0) && (
                                                  <>
                                                    <span className="text-muted-foreground">•</span>
                                                    <span className="font-medium text-primary">{formatBytes(room.storageUsage)}</span>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          </div>

                                          {room.description && (
                                            <p className="text-sm text-card-foreground line-clamp-2 min-h-[2.5em]">
                                              {room.description}
                                            </p>
                                          )}
                                          <div className="flex flex-col gap-3 pt-4 border-t mt-auto">
                                            <div className="flex items-center gap-3">
                                              <div className="flex -space-x-2 overflow-hidden items-center hover:space-x-1 transition-all">
                                                {room.users?.slice(0, 5).map(u => (
                                                  <Avatar key={u.id} className="w-7 h-7 transition-transform hover:scale-110 hover:z-10">
                                                    <AvatarImage src={u.photoUrl ?? undefined} />
                                                    <AvatarFallback className="text-meta-sm">{u.name.substring(0, 1)}</AvatarFallback>
                                                  </Avatar>
                                                ))}
                                                {room.users && room.users.length > 5 && (
                                                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-muted text-meta-sm font-semibold text-card-foreground z-10">
                                                    +{room.users.length - 5}
                                                  </div>
                                                )}
                                              </div>
                                              <div className="flex items-center justify-center px-2 py-0.5 rounded-full bg-muted border border-border text-xs font-semibold text-card-foreground gap-1.5" title={t`Total Members`}>
                                                <User className="w-3 h-3 text-muted-foreground" />
                                                {room.users.length}
                                              </div>
                                            </div>
                                            <Button
                                              size="sm"
                                              variant="default"
                                              onClick={() => handleJoinClick(room)}
                                              className="w-full h-8"
                                            >
                                              <Trans>Join</Trans>
                                            </Button>
                                          </div>
                                        </CardContent>
                                      </Card>
                                    )
                                  })}
                                </div>
                                {rooms.filter(r => r.visibility === 'listed' && !r.isOneToOne && !hasJoined(r.id)).length === 0 && (
                                  <div className="text-center py-16 text-muted-foreground bg-background rounded-xl border border-dashed">
                                    <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                    <p className="text-lg font-medium"><Trans>No available channels.</Trans></p>
                                    <p className="text-sm text-muted-foreground mt-1"><Trans>You have already joined all listed channels.</Trans></p>
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>}
                    </div>
                  )}

                  {/* Admin Views - Owner or Admin with canViewAdminPanel */}
                  {isAdmin && (
                    <>
                      {currentView === 'rooms' && (
                        <WSAdminRoomsPage />
                      )}
                      {currentView === 'settings' && (
                        <WSAdminSettings workspace={workspace} onUpdate={reload} />
                      )}
                      {currentView === 'members' && (
                        <WSAdminMembers workspace={workspace} />
                      )}
                      {currentView === 'storage' && (
                        <WSAdminStorage workspace={workspace} />
                      )}
                      {currentView === 'inquiry' && (
                        <WSAdminInquiry workspace={workspace} />
                      )}
                      {currentView === 'company' && (
                        <WSAdminCompanyPage workspace={workspace} onUpdate={reload} />
                      )}
                      {currentView === 'board' && (
                        <WSAdminBoardPolicyPage workspace={workspace} />
                      )}
                      {currentView === 'gigapod' && (
                        <WSAdminGigapodPage workspace={workspace} />
                      )}
                    </>
                  )}
                </>
              )
            )}
          </div>
        </main>

        {/* Right Sidebar - Logic: Show only if admin AND Desktop */}
        {workspace && isAdmin && !isMobileOrTablet && (
          <aside className="w-64 flex flex-col shrink-0 border-l border-border bg-background">
            <WSAdminSidebarContent
              workspace={workspace}
              currentView={currentView}
              onChangeView={handleViewChange}
            />
          </aside>
        )}
      </div>

      {showCreateRoomModal && <CreateRoomModal show={showCreateRoomModal} setShow={setShowCreateRoomModal} workspaceId={workspaceId || null} />}
      {optionModalRoom && (
        <MyRoomOptionModal
          room={optionModalRoom as any}
          workspace={workspaces.find(w => w.id === workspaceId)}
          isOpen={!!optionModalRoom}
          onOpenChange={(open) => { if (!open) { setOptionModalRoom(null); forceUpdate(); } }}
        />
      )}

      <AlertDialog open={alertConfig.open} onOpenChange={(open) => { if (!open) setAlertConfig(prev => ({ ...prev, open: false })) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertConfig.title}</AlertDialogTitle>
            {alertConfig.description && <AlertDialogDescription>{alertConfig.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAlertConfig(prev => ({ ...prev, open: false }))}><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction onClick={() => alertConfig.onConfirm?.()}>{alertConfig.confirmText || <Trans>Confirm</Trans>}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default WorkspaceViewPage;
